import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import LockedFeature from "@/components/LockedFeature";
import { FileText, Sparkles, Download, Eye, Loader2, X, CheckCircle2 } from "lucide-react";

export default function Policies() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(null); // name being generated or "ALL"
  const [view, setView] = useState(null);

  const load = useCallback(() => api.get("/policies").then((r) => setData(r.data)), []);
  useEffect(() => { load(); api.get("/billing/status").then((r) => setActive(r.data.active)); }, [load]);

  // Poll while any policy is still generating in the background.
  useEffect(() => {
    if (!data) return;
    const generating = data.policies.some((p) => p.status === "generating");
    if (!generating) { setBusy(null); return; }
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);

  const generate = async (names, key) => {
    setBusy(key);
    try {
      const { data: res } = await api.post("/policies/generate", { names });
      toast.success(`Queued ${res.count} ${res.count === 1 ? "policy" : "policies"} — generating…`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); setBusy(null); }
  };

  const openView = async (id) => {
    const { data: p } = await api.get(`/policies/${id}`);
    setView(p);
  };

  if (!data) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  if (active === false) return <div className="p-8 max-w-3xl"><LockedFeature feature="Policy Generator" /></div>;
  const missing = data.policies.filter((p) => p.status === "missing" || p.status === "error").map((p) => p.name);
  const anyGenerating = data.policies.some((p) => p.status === "generating");

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-1">Feature B</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Policy Generator</h1>
          <p className="text-slate-500 text-sm mt-1">{data.generated}/{data.total} required policies generated, each mapped to its NIST controls.</p>
        </div>
        <div className="flex items-center gap-3">
          {data.generated > 0 && (
            <a href={`${API}/policies/binder/download`} data-testid="download-binder-btn"
              className="border border-slate-300 px-5 py-2.5 rounded-md text-sm font-medium flex items-center gap-2 hover:bg-slate-50 transition-colors">
              <Download className="h-4 w-4" /> Download binder ({data.generated})
            </a>
          )}
          <button onClick={() => generate(null, "ALL")} disabled={busy || anyGenerating || missing.length === 0} data-testid="generate-all-btn"
            className="bg-brand text-white px-5 py-2.5 rounded-md text-sm font-medium flex items-center gap-2 hover:bg-brand-secondary transition-colors disabled:opacity-50">
            {(busy === "ALL" || anyGenerating) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {anyGenerating ? "Generating…" : `Generate ${missing.length} Missing`}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.policies.map((p) => (
          <PolicyCard key={p.name} p={p} busy={busy} onView={openView} onGenerate={generate} />
        ))}
      </div>

      {view && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setView(null)} data-testid="policy-view-dialog">
          <div className="bg-white rounded-md max-w-3xl w-full max-h-[85vh] overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="font-display font-bold">{view.name}</div>
              <button onClick={() => setView(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <pre className="p-6 text-sm whitespace-pre-wrap font-sans leading-relaxed text-slate-700">{view.content}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function PolicyStatusBadge({ status }) {
  if (status === "generated")
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-success"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>;
  if (status === "generating")
    return <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-accent"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</span>;
  if (status === "error")
    return <span className="text-xs font-semibold text-danger">Failed</span>;
  return <span className="text-xs font-semibold text-slate-400">Not generated</span>;
}

function PolicyCardActions({ p, busy, onView, onGenerate }) {
  if (p.status === "generated")
    return (
      <>
        <button onClick={() => onView(p.id)} data-testid={`view-${p.id}`} className="flex-1 border border-slate-300 rounded-md py-1.5 text-xs font-medium hover:bg-slate-50 flex items-center justify-center gap-1"><Eye className="h-3.5 w-3.5" /> View</button>
        <a href={`${API}/policies/${p.id}/download/pdf`} className="flex-1 bg-brand text-white rounded-md py-1.5 text-xs font-medium hover:bg-brand-secondary flex items-center justify-center gap-1" data-testid={`download-pdf-${p.id}`}><Download className="h-3.5 w-3.5" /> PDF</a>
        <a href={`${API}/policies/${p.id}/download`} className="border border-slate-300 rounded-md py-1.5 px-2.5 text-xs font-medium hover:bg-slate-50 flex items-center justify-center gap-1" data-testid={`download-${p.id}`} title="Download Markdown">.md</a>
      </>
    );
  if (p.status === "generating")
    return <button disabled className="flex-1 bg-slate-100 text-slate-400 rounded-md py-1.5 text-xs font-medium flex items-center justify-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…</button>;
  return (
    <button onClick={() => onGenerate([p.name], p.name)} disabled={!!busy} data-testid={`gen-${p.name.replace(/\W/g, "")}`}
      className="flex-1 bg-slate-900 text-white rounded-md py-1.5 text-xs font-medium hover:bg-brand-secondary flex items-center justify-center gap-1 disabled:opacity-50">
      <Sparkles className="h-3.5 w-3.5" /> {p.status === "error" ? "Retry" : "Generate"}
    </button>
  );
}

function PolicyCard({ p, busy, onView, onGenerate }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm flex flex-col" data-testid={`policy-${p.name.replace(/\W/g, "")}`}>
      <div className="flex items-start justify-between mb-2">
        <FileText className="h-5 w-5 text-brand-accent" />
        <PolicyStatusBadge status={p.status} />
      </div>
      <div className="font-display font-bold text-sm leading-snug mb-1">{p.name}</div>
      <div className="text-xs text-slate-500 mb-3">{p.family}</div>
      <div className="flex flex-wrap gap-1 mb-4">
        {p.satisfies.map((s) => <span key={s} className="font-mono text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{s}</span>)}
      </div>
      <div className="mt-auto flex gap-2">
        <PolicyCardActions p={p} busy={busy} onView={onView} onGenerate={onGenerate} />
      </div>
    </div>
  );
}
